import React, { useContext, useState } from 'react';
import PropTypes from 'prop-types';
import dynamic from 'next/dynamic';
import clsx from 'clsx';
import { Dicon, Button } from 'edcom-ui';

import Error from '../_error';

import content from '../../services/content';
import { CollectionsProvider } from '../../services/CollectionsContext';

import Head from '../../components/head';
import Breadcrumbs from '../../components/Breadcrumbs';
import GameStructuredData from '../../components/StructuredData/GameStructuredData';
import AdminLinks from '../../components/AdminLinks/AdminLinks';
import CategoryTree from '../../components/CategoryTree/CategoryTree';
import RelatedGuidedLesson from '../../components/RelatedGuidedLessons';
import GuidedLessonAction from '../../components/game/GuidedLessonAction';
import GameIFrame, { openGameIFrame } from '../../components/game/GameIFrame';
import CollectionsBookmark from '../../components/Collections/CollectionsBookmark';

import styles from './game.module.less';
import AccountContext from '../../services/AccountContext';

const AssignmentsModal = dynamic(
  () => import('../../components/AssignmentsModal'),
  {
    ssr: false,
  }
);

const RelatedLearningResources = dynamic(
  () => import('../../components/RelatedLearningResources'),
  { ssr: false }
);

const ShareOptions = dynamic(() => import('../../components/ShareOptions'), {
  ssr: false,
});

const Game = ({
  id,
  title,
  urlName,
  internalName,
  metaTitle = '',
  metaDescription = '',
  indexed = false,
  text = '',
  updatedAt,
  createdAt,
  standards = [],
  grades = [],
  subjects = [],
  mainImage = null,
  guidedLesson = null,
  course = null,
  allowAnonPlay = false,
  type = 'game',
  error = false,
}) => {
  const [assignmentsIsOpen, setAssignmentsIsOpen] = useState(false);
  const handleMainAction = event => {
    event.preventDefault();
    openGameIFrame();
  };

  const { memberId } = useContext(AccountContext);

  const contentTypeMap = {
    game: {
      path: 'games',
      breadcrumb: 'Games',
      title: 'Online game',
    },
    song: {
      path: 'songs',
      breadcrumb: 'Songs',
      title: 'Song video',
    },
    story: {
      path: 'stories',
      breadcrumb: 'Stories',
      title: 'Interactive story',
    },
  };

  if (!mainImage)
    mainImage = `https://cdn.education.com/files/static/game-images/${urlName}.jpg`;

  const defaultLessonPath = !guidedLesson
    ? `singlePlay:${id},${subjects[0]?.internalName || ''},`
    : `${course.subject.toLowerCase()},${course.internalName},${
        guidedLesson.urlName
      },${internalName}`;

  return (
    <>
      <Head
        description={metaDescription}
        index={indexed}
        image={mainImage}
        title={metaTitle}
      >
        <title>{metaTitle}</title>
        <meta property="og:type" content="educationdotcom:game" />
        <GameStructuredData
          createdAt={createdAt ? createdAt.replace(/\s+/, 'T') : ''}
          updatedAt={updatedAt ? updatedAt.replace(/\s+/, 'T') : null}
          path={`/game/${urlName}/`}
          image={mainImage}
          title={title}
          description={metaDescription}
        />
      </Head>
      <div
        className={clsx('page-width', 'typography-small', styles.gameDetail)}
      >
        {error ? (
          <Error />
        ) : (
          <CollectionsProvider>
            <Breadcrumbs
              className={styles.breadcrumbs}
              title={title}
              grades={grades}
              path={contentTypeMap[type].path}
              subjects={subjects}
              contentType={contentTypeMap[type].breadcrumb}
            />
            <div>
              <h3 className={styles.contentTitle}>
                <Dicon name={type} theme="dark" />{' '}
                <span className={styles.contentName}>
                  {contentTypeMap[type].title}
                </span>
              </h3>
              <h1>{title}</h1>
              {!!memberId && (
                <ShareOptions
                  name={title}
                  imageSrc={mainImage}
                  contentType={'game'}
                  contentId={id}
                />
              )}
            </div>
            <div className={styles.content}>
              <div className={styles.contentInfo}>
                <div className={styles.actionMain}>
                  <Button
                    size="normal"
                    onClick={handleMainAction}
                    href="#game-section"
                    target="_blank"
                  >
                    Play
                  </Button>
                  <Button
                    size="normal"
                    color="secondary"
                    className={'add-to-assignment'}
                    onClick={() => setAssignmentsIsOpen(true)}
                  >
                    Assign this Game
                  </Button>
                </div>
                <div className={styles.actionSecondary}>
                  <CollectionsBookmark
                    type={type}
                    id={id}
                    title={title}
                    thumbnail={mainImage}
                  />
                </div>

                <div dangerouslySetInnerHTML={{ __html: text }} />
                {guidedLesson && (
                  <GuidedLessonAction href={guidedLesson?.urlName} />
                )}
                <CategoryTree
                  resource={{
                    grades: grades,
                    subjects: subjects,
                    standards: standards,
                  }}
                />
              </div>
              <div className={styles.gameFrame}>
                <GameIFrame
                  defaultLessonPath={defaultLessonPath}
                  internalName={internalName}
                  imgSrc={mainImage}
                  alt={'Game'}
                  id={id}
                  title={title}
                  subject={
                    !course
                      ? subjects[0]?.urlName || ''
                      : course?.subject.toLowerCase()
                  }
                  grade={grades[0].urlName}
                  allowAnonPlay={allowAnonPlay}
                  inGuidedLesson={!!guidedLesson}
                />
              </div>
            </div>
            {!guidedLesson ? null : <RelatedGuidedLesson {...guidedLesson} />}
            <RelatedLearningResources type="game" id={id} />
          </CollectionsProvider>
        )}
      </div>
      <AssignmentsModal
        itemsToAdd={[{ type: 'exercise', internalName: id }]}
        onClose={() => setAssignmentsIsOpen(false)}
        isOpen={assignmentsIsOpen}
        section="Game detail"
        contentId={id}
      />
      <AdminLinks>
        <a
          href={`/secure/exercises/exercise.php?nocache&id=${id}`}
          target="_blank"
        >
          Edit Exercise
        </a>
      </AdminLinks>
    </>
  );
};

Game.getInitialProps = async ({ asPath, req, query }) => {
  const contentService = content(req);
  const { urlName } = query;

  try {
    const response = await contentService.getGameDetails(urlName);
    return {
      ...response,
      pageKey: asPath,
    };
  } catch (error) {
    console.error('InitialProps Error', error);
    return { error };
  }
};

const categoryShape = PropTypes.shape({
  urlName: PropTypes.string,
  internalName: PropTypes.string,
  name: PropTypes.string,
});

Game.propTypes = {
  id: PropTypes.string.isRequired,
  title: PropTypes.string.isRequired,
  urlName: PropTypes.string.isRequired,
  internalName: PropTypes.string.isRequired,
  metaTitle: PropTypes.string,
  description: PropTypes.string.isRequired,
  metaDescription: PropTypes.string,
  indexed: PropTypes.bool,
  text: PropTypes.string.isRequired,
  updatedAt: PropTypes.string,
  createdAt: PropTypes.string,
  standards: PropTypes.arrayOf(categoryShape),
  grades: PropTypes.arrayOf(categoryShape),
  subjects: PropTypes.arrayOf(categoryShape),
  type: PropTypes.string,
  mainImage: PropTypes.string,
  guidedLesson: PropTypes.object,
  course: PropTypes.object,
  allowAnonPlay: PropTypes.bool,
  error: PropTypes.object,
};

export default Game;
